import { Instagram, Facebook, Youtube } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { lockScroll, unlockScroll } from "@/lib/scroll-lock";

const legalLinks = [
  { labelKey: "footer.termsConditions", to: "/terms" },
  { labelKey: "footer.privacyPolicy", to: "/privacy" },
  { labelKey: "footer.refundPolicy", to: null },
  { labelKey: "footer.shippingReturns", to: "/shipping-returns" },
  { labelKey: "footer.cookiePolicy", to: "/cookies" },
];

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenu = ({ isOpen, onClose }: MobileMenuProps) => {
  const { t } = useLanguage();

  const navItems = [
    { label: t("nav.shopWatches"), to: "/archive" },
    { label: t("nav.shopJewelry"), to: "/archive" },
    { label: t("nav.sellWatchJewelry"), to: "/sell" },
    { label: t("nav.privateEmailList"), to: "/email-list" },
    { label: t("nav.about"), to: "/about" },
    { label: t("nav.sold"), to: "/sold" },
  ];

  useEffect(() => {
    if (isOpen) {
      lockScroll();
    } else {
      unlockScroll();
    }
    return () => { unlockScroll(); };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-background/60 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Drawer panel sliding from the left */}
      <div
        className={`fixed top-0 left-0 bottom-0 z-[70] w-full max-w-[420px] bg-popover border-r border-border flex flex-col transition-transform duration-300 ease-out lg:hidden ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div
          className="flex items-center justify-end px-5"
          style={{ height: "var(--header-height)" }}
        >
          <button
            onClick={onClose}
            className="relative z-[80] text-foreground hover:text-accent transition-colors w-10 h-10 flex items-center justify-center"
            aria-label="Close menu"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.2">
              <line x1="2" y1="2" x2="18" y2="18" />
              <line x1="18" y1="2" x2="2" y2="18" />
            </svg>
          </button>
        </div>

        <nav className="flex-1 flex flex-col items-stretch justify-center -mt-[8vh]">
          {navItems.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              onClick={onClose}
              className="flex items-center justify-center px-6 py-4 font-sans text-[14px] font-normal uppercase tracking-[0.1em] text-foreground hover:bg-accent hover:text-accent-foreground transition-all duration-200"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center justify-center gap-7 pb-4">
          <Instagram size={18} strokeWidth={1.3} className="text-muted-foreground" />
          <Facebook size={18} strokeWidth={1.3} className="text-muted-foreground" />
          <div className="w-[18px] h-[18px] text-muted-foreground flex items-center justify-center">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.3">
              <path d="M9 12a4 4 0 1 0 4-4H9v4z" />
              <path d="M15 8a4 4 0 1 0 0 8" />
              <rect x="2" y="4" width="20" height="16" rx="2" />
            </svg>
          </div>
          <Youtube size={18} strokeWidth={1.3} className="text-muted-foreground" />
        </div>

        {/* Legal links */}
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1.5 px-6 pb-8">
          {legalLinks.map((link) =>
            link.to ? (
              <Link
                key={link.labelKey}
                to={link.to}
                onClick={onClose}
                className="body-x-small text-foreground/60 hover:text-foreground transition-colors"
              >
                {t(link.labelKey)}
              </Link>
            ) : (
              <span
                key={link.labelKey}
                className="body-x-small text-foreground/60 cursor-default"
              >
                {t(link.labelKey)}
              </span>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
